/**
 * Quota Runtime V2 display helpers.
 * When V2 is enabled, runtime UX must use session + monthly terminology only.
 * Daily fields must never drive Start Class or labels under V2.
 */
import type { RuntimeQuotaResponse } from '../../modules/tutor/api/quotaRuntime.client';
import {
  formatSessionCountdown,
  sessionRemainingSecondsFromExpiresAt,
} from '../../modules/tutor/api/quotaRuntime.client';
import {
  experienceQuotaMonthlyExhausted,
  type ExperienceQuotaFields,
} from './tutorLiveQuotaUtils';

export {
  formatSessionCountdown,
  sessionRemainingSecondsFromExpiresAt,
};

export type RuntimeV2DisplayLabels = {
  sessionHeading: string;
  sessionCountdown: string;
  maxSessionDuration: string;
  monthlyBudgetHeading: string;
  monthlyUsed: string;
  monthlyRemaining: string;
  monthlyUsageLine: string;
  exhaustedTitle: string;
  exhaustedBody: string;
};

export function formatMinutesCompact(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  return `${Math.max(0, Math.floor(minutes))} min`;
}

export function resolveRuntimeV2Display(
  q: RuntimeQuotaResponse | null,
  nowMs: number = Date.now(),
): RuntimeV2DisplayLabels {
  const expiresAt =
    q?.active_session?.expires_at ?? q?.session_expires_at ?? null;
  const remSec =
    expiresAt != null
      ? sessionRemainingSecondsFromExpiresAt(expiresAt, nowMs)
      : q?.session_remaining_seconds ?? null;
  const maxMin = q?.max_session_minutes ?? null;
  const used = q?.monthly_used_minutes ?? 0;
  const budget = q?.monthly_budget_minutes ?? null;
  const remaining = q?.monthly_remaining_minutes ?? null;

  return {
    sessionHeading: 'Current Session',
    sessionCountdown:
      remSec != null ? formatSessionCountdown(remSec) : maxMin != null ? formatSessionCountdown(maxMin * 60) : '—',
    maxSessionDuration: formatMinutesCompact(maxMin),
    monthlyBudgetHeading: 'Monthly AI Budget',
    monthlyUsed: formatMinutesCompact(used),
    monthlyRemaining: formatMinutesCompact(remaining),
    monthlyUsageLine:
      budget != null ? `${used} / ${budget} min` : used > 0 ? `${used} min used` : '—',
    exhaustedTitle: 'Monthly AI budget reached',
    exhaustedBody:
      'Your monthly AI tutor budget is used up. Try again next month or ask your teacher for help.',
  };
}

/** Start Class authority under Runtime V2: monthly budget only (active session handled separately). */
export function isRuntimeV2StartBlocked(q: RuntimeQuotaResponse | null): boolean {
  if (!q) return false;
  if (q.quota_exhausted === true) return true;
  if (q.monthly_remaining_seconds != null && q.monthly_remaining_seconds <= 0) return true;
  if (q.monthly_remaining_minutes != null && q.monthly_remaining_minutes <= 0) return true;
  return false;
}

/** Map legacy /quota/me monthly fields when bootstrapping before /runtime/quota loads. */
export function isMonthlyExhaustedFromLegacyMe(q: ExperienceQuotaFields | null): boolean {
  return experienceQuotaMonthlyExhausted(q);
}

export const RUNTIME_V2_GOVERNANCE_HELP =
  'Flexible and Strict apply to monthly experience budgets only. Unused monthly minutes may be shared across tutors in Flexible mode; Strict keeps each tutor within its monthly allocation.';

/** Shared plan / admin entitlement labels gated by Runtime V2 capability. */
export function planEntitlementDailyLabel(runtimeV2: boolean): string {
  return runtimeV2
    ? 'Daily AI Minutes (optional / analytics)'
    : 'Daily AI Minutes per Student';
}

export function planMaxSessionLabel(): string {
  return 'Maximum Session Duration';
}

export function planIdleTimeoutLabel(): string {
  return 'Idle Timeout';
}

export function planEntitlementMonthlyLabel(runtimeV2: boolean): string {
  return runtimeV2 ? 'Monthly AI Budget per Student' : 'Monthly AI Minutes per Student';
}

export type FormatPlanQuotaOpts = {
  maxSessionMinutes?: number | null;
  idleTimeoutSeconds?: number | null;
};

export function formatPlanQuotaLine(
  runtimeV2: boolean,
  dailyMinutes: number | null | undefined,
  monthlyMinutes: number | null | undefined,
  opts?: FormatPlanQuotaOpts,
): string {
  const monthly = monthlyMinutes != null ? `${monthlyMinutes} min/month` : '—';
  if (runtimeV2) {
    const maxSession =
      opts?.maxSessionMinutes != null ? `${opts.maxSessionMinutes} min session` : null;
    const idle =
      opts?.idleTimeoutSeconds != null ? `${opts.idleTimeoutSeconds}s idle` : null;
    const parts = [
      `Monthly AI Budget: ${monthly}`,
      maxSession,
      idle,
    ].filter(Boolean);
    return parts.join(' · ');
  }
  const daily = dailyMinutes != null ? `${dailyMinutes} min/day` : '—';
  return `${daily} · ${monthly}`;
}

export function studentQuotaDailyHeading(runtimeV2: boolean): string {
  return runtimeV2 ? 'Session Policy' : 'Daily quota per student';
}

export function studentQuotaMonthlyHeading(runtimeV2: boolean): string {
  return runtimeV2 ? 'Monthly AI Budget per student' : 'Monthly quota per student';
}
